import React, { useEffect, useRef, useState } from 'react';

// A posterless video needs a real decoded frame. Only videos near the viewport
// receive a source; removing it when they leave aborts further media requests.
export default function VideoFramePreview({ url, className = '' }) {
  const frameRef = useRef(null);
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!url || !frameRef.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { rootMargin: '100px' });
    observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, [url]);

  useEffect(() => {
    if (!visible) return undefined;
    const video = videoRef.current;
    if (video && video.getAttribute('src') !== url) {
      // React updates src before passive-effect cleanup on a URL change.
      // Restore it after that cleanup (and during StrictMode effect replay).
      video.setAttribute('src', url);
      video.load?.();
    }
    return () => {
      video?.pause?.();
      video?.removeAttribute('src');
      video?.load?.();
    };
  }, [visible, url]);

  const seekFirstFrame = (event) => {
    const video = event.currentTarget;
    const duration = video.duration;
    const frameTime = Number.isFinite(duration) && duration > 0 ? Math.min(0.1, duration / 2) : 0.1;
    try { video.currentTime = frameTime; } catch { /* Browser will retain its initial frame. */ }
  };

  return (
    <span className={`video-frame-preview ${className}`} ref={frameRef} aria-hidden="true">
      {visible && url && (
        <video ref={videoRef} src={url} preload="metadata" muted playsInline onLoadedMetadata={seekFirstFrame} />
      )}
    </span>
  );
}
